import React from 'react';
import { TableCell, TableRow, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { SEARCE_COLORS } from '../../theme';
import { TABLE_STYLES } from '../../constants/tableStyles';
import type { DataTableColumnDef, DataTableColumnMeta } from './dataTableTypes';
import type { TableTotalsResult } from '../../utils/tableTotals';
import type { TableTotalsFormat } from '../../constants/tableTotalsConfig';
import {
  formatCompactAbbrev,
  formatCurrency,
  formatCurrencyFull,
  formatNumber,
  formatNumberFull,
  formatPercentage,
  formatPercentageFull,
} from '../../utils/FormatUtils';
import { NumericValueWithTooltip } from '../common/NumericValueWithTooltip';

function formatTotalDisplay(value: number, format: TableTotalsFormat = 'number'): string {
  switch (format) {
    case 'currency':
      return formatCurrency(value);
    case 'compact':
      return formatCompactAbbrev(value);
    case 'percentage':
      return formatPercentage(value);
    case 'number':
    default:
      return formatNumber(value);
  }
}

function formatTotalFull(value: number, format: TableTotalsFormat = 'number'): string {
  switch (format) {
    case 'currency':
      return formatCurrencyFull(value);
    case 'compact':
      return formatNumberFull(value);
    case 'percentage':
      return formatPercentageFull(value);
    case 'number':
    default:
      return formatNumberFull(value);
  }
}

function TotalCellContent({ value, format }: { value: number; format?: TableTotalsFormat }) {
  const fmt = format ?? 'number';
  const display = formatTotalDisplay(value, fmt);
  const full = formatTotalFull(value, fmt);
  return (
    <NumericValueWithTooltip
      display={display}
      fullValue={full}
      variant="body2"
      typographySx={{ fontSize: '0.75rem', fontWeight: 700 }}
      align="inherit"
    />
  );
}

export type TableTotalsFooterRowProps<TData> = {
  columns: DataTableColumnDef<TData>[];
  totals: TableTotalsResult;
  /** Merged into each footer cell (e.g. TanStack width). */
  resolveCellSx?: (col: DataTableColumnDef<TData>, colIndex: number) => SxProps<Theme>;
  /** Shell for first visible column when not the label column. */
  useFirstColumnShell?: boolean;
};

const footerRowSx: SxProps<Theme> = {
  backgroundColor: alpha(SEARCE_COLORS.neutral_100, 0.85),
  borderTop: `2px solid ${SEARCE_COLORS.neutral_300}`,
  '& .MuiTableCell-root': {
    fontWeight: 700,
    borderBottom: 'none',
  },
};

/**
 * Footer row aligned to visible DataTable columns; numeric totals from `tableTotalsConfig`.
 */
export function TableTotalsFooterRow<TData>({
  columns,
  totals,
  resolveCellSx,
  useFirstColumnShell = true,
}: TableTotalsFooterRowProps<TData>): React.ReactElement {
  return (
    <TableRow sx={footerRowSx}>
      {columns.map((col, colIndex) => {
        const m = col.meta;
        const isFirstVisible = colIndex === 0;
        const useFirstShell = isFirstVisible && useFirstColumnShell && !m?.preserveMetaAlignWhenFirstVisible;
        const isLabel = col.id === totals.labelColumnId;
        const computed = totals.totalsByColumnId.get(col.id);
        const align = useFirstShell ? 'left' : m?.align ?? 'center';

        return (
          <TableCell
            key={col.id}
            align={align}
            colSpan={m?.colSpan}
            sx={[
              TABLE_STYLES.cell as any,
              { py: 0.85, verticalAlign: 'middle' },
              (m?.cellSx as any) ?? {},
              ...(resolveCellSx ? [resolveCellSx(col, colIndex) as any] : []),
            ]}
          >
            {isLabel ? (
              <Typography
                variant="body2"
                component="span"
                sx={{ fontSize: '0.75rem', fontWeight: 700, color: SEARCE_COLORS.textPrimary }}
              >
                {totals.footerLabel}
              </Typography>
            ) : computed ? (
              <TotalCellContent value={computed.value} format={computed.spec.format} />
            ) : null}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

/** Footer row for custom MUI tables with explicit column order (no DataTableColumnDef). */
export type MuiTableTotalsColumn = {
  id: string;
  align?: 'left' | 'center' | 'right';
  colSpan?: number;
  sx?: SxProps<Theme>;
  /** e.g. Project Details `data-pd-col` for column visibility CSS. */
  dataPdCol?: string;
};

export function MuiTableTotalsFooterRow({
  columns,
  totals,
}: {
  columns: MuiTableTotalsColumn[];
  totals: TableTotalsResult;
}): React.ReactElement {
  return (
    <TableRow sx={footerRowSx}>
      {columns.map((col) => {
        const isLabel = col.id === totals.labelColumnId;
        const computed = totals.totalsByColumnId.get(col.id);
        return (
          <TableCell
            key={col.id}
            align={col.align ?? (isLabel ? 'left' : 'center')}
            colSpan={col.colSpan}
            {...(col.dataPdCol ? { 'data-pd-col': col.dataPdCol } : {})}
            sx={[
              TABLE_STYLES.cell as any,
              { py: 0.85, fontSize: '0.75rem' },
              (col.sx as any) ?? {},
            ]}
          >
            {isLabel ? (
              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                {totals.footerLabel}
              </Typography>
            ) : computed ? (
              <TotalCellContent value={computed.value} format={computed.spec.format} />
            ) : null}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
